import { getPerformVehicles } from '@common/permissions/selectors';
import { Loadable } from '@data/loadable';
import { containsElectricVehicleType, getSelectedVehicleIds } from '@data/selectors';
import { hideSidebar, showSidebar, SidebarData, sidebarData } from '@features/sidebar';
import { getActiveDateRange, getDateRange } from '@features/ui/selectors';
import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { State } from 'src/setup/types';
import { DateRange, Id } from 'src/types';

import { VEHICLE_ROUTE } from '../../constants/routes';
import Content from './Content';
import useVehicleContentLoader from './useVehicleContentLoader';

type Props = {
    onShowDetails: (data: SidebarData) => void;
    onHideDetails: () => void;
    onUpdateSidebarData: (data: SidebarData) => void;
    vehicles?: Id[];
    dateRange: DateRange;
    performVehicles?: Id[];
    activeDateRange: DateRange;
    containsElectric: boolean;
    selectedSidebarData?: SidebarData;
};

export const Entry = ({
    onShowDetails,
    onHideDetails,
    onUpdateSidebarData,
    vehicles = [],
    dateRange,
    performVehicles = [],
    activeDateRange,
    containsElectric,
    selectedSidebarData,
}: Props) => {
    useEffect(() => {
        if (vehicles.length === 0) {
            onHideDetails();
        }
    }, [onHideDetails, vehicles]);

    const { parentsWithChildren, attributes } = useVehicleContentLoader({
        dateRange: activeDateRange,
        vehicles,
        performVehicles,
        containsElectric,
    });

    const loadedAttributes = useMemo(
        () =>
            Loadable.cata(
                attributes,
                (d) => d,
                () => [],
                () => [],
                () => []
            ),
        [attributes]
    );

    useEffect(() => {
        if (!selectedSidebarData?.id || loadedAttributes.length === 0) {
            return;
        }

        if ((selectedSidebarData.attributes?.length ?? 0) > 0) {
            return;
        }

        onUpdateSidebarData({
            ...selectedSidebarData,
            attributes: loadedAttributes,
        });
    }, [loadedAttributes, onUpdateSidebarData, selectedSidebarData]);

    return (
        <div>
            <Content
                dateRange={dateRange}
                vehicles={vehicles}
                onItemClick={onShowDetails}
                parentsWithChildren={parentsWithChildren}
                attributes={attributes}
            />
        </div>
    );
};

export const mapDispatchToProps = (dispatch: Dispatch) => ({
    onHideDetails: () => dispatch(hideSidebar()),
    onShowDetails: (data: SidebarData) => dispatch(showSidebar({ data, type: VEHICLE_ROUTE })),
    onUpdateSidebarData: (data: SidebarData) => dispatch(showSidebar({ data, type: VEHICLE_ROUTE })),
});

export default connect(
    (state: State) => ({
        vehicles: getSelectedVehicleIds(state),
        dateRange: getDateRange(state),
        activeDateRange: getActiveDateRange(state),
        performVehicles: getPerformVehicles(state),
        containsElectric: containsElectricVehicleType(state),
        selectedSidebarData: sidebarData(state, VEHICLE_ROUTE),
    }),
    mapDispatchToProps
)(Entry);
